import api from "../utils/api";
import { AxiosResponse } from "axios";
import {
  CreateWorkshopDto,
  UpdateWorkshopDto,
  WorkshopDto,
  CreateWorkshopInstanceDto,
  UpdateWorkshopInstanceDto,
  WorkshopInstanceDto,
  WorkshopParticipantDto,
  FeedbackQuestionDto,
  WorkshopInstanceUserFeedbackDto,
  WorkshopInstanceShortDto,
  WorkshopInstanceFeedbackDto,
} from "../types/workshopTypes";

//-----------------------------------------------------------------------------------------------------------------------
// GET ROUTES

/**
 * Get all workshops
 * @returns An array of all workshops
 */
export const getWorkshops = async (): Promise<AxiosResponse<WorkshopDto[]>> => {
  return await api.get<WorkshopDto[]>("/workshops/");
};

/**
 * Get a specific workshop by ID
 * @param workshopId - The ID of the workshop
 * @returns The workshop details
 */
export const getWorkshopDetails = async (workshopId: number): Promise<AxiosResponse<WorkshopDto>> => {
  return await api.get<WorkshopDto>(`/workshops/${workshopId}`);
};

/**
 * Get all workshop instances for a specific workshop
 * @param workshopId - The ID of the workshop
 * @returns An array of workshop instances for the workshop
 */
export const getWorkshopInstances = async (workshopId: number): Promise<AxiosResponse<WorkshopInstanceDto[]>> => {
  return await api.get<WorkshopInstanceDto[]>(`/workshops/${workshopId}/instances`);
};

/**
 * Get a specific workshop instance by ID
 * @param workshopId - The ID of the workshop
 * @param workshopInstanceId - The ID of the workshop instance
 * @returns The workshop instance details
 */
export const getWorkshopInstanceDetails = async (
  workshopId: number,
  workshopInstanceId: number
): Promise<AxiosResponse<WorkshopInstanceDto>> => {
  return await api.get<WorkshopInstanceDto>(`/workshops/${workshopId}/instances/${workshopInstanceId}`);
};

/**
 * Get the feedback questions for a specific workshop instance
 * @param workshopId - The ID of the workshop
 * @param workshopInstanceId - The ID of the workshop instance
 * @returns An array of feedback questions for the workshop instance
 */
export const getFeedbackQuestions = async (
  workshopId: number,
  workshopInstanceId: number
): Promise<AxiosResponse<FeedbackQuestionDto[]>> => {
  return await api.get<FeedbackQuestionDto[]>(
    `/workshops/${workshopId}/instances/${workshopInstanceId}/feedback-questions`
  );
};

/**
 * Get all workshop instances that are pending feedback
 * @returns An array of workshop instances that are pending feedback
 */
export const getPendingFeedbackWorkshopInstances = async (): Promise<AxiosResponse<WorkshopInstanceShortDto[]>> => {
  return await api.get<WorkshopInstanceShortDto[]>(`/workshops/members/pending-feedback`);
};

/**
 * Get the feedback for a specific workshop instance
 * @param workshopId - The ID of the workshop
 * @param workshopInstanceId - The ID of the workshop instance
 * @returns The feedback details for the workshop instance
 */
export const getWorkshopInstanceFeedback = async (
  workshopId: number,
  workshopInstanceId: number
): Promise<AxiosResponse<WorkshopInstanceFeedbackDto>> => {
  return await api.get<WorkshopInstanceFeedbackDto>(
    `/workshops/${workshopId}/instances/${workshopInstanceId}/feedback`
  );
};

//-----------------------------------------------------------------------------------------------------------------------
// CREATE ROUTES

/**
 * Create a new workshop
 * @param workshopData - The workshop data to create
 * @returns The created workshop
 */
export const createWorkshop = async (workshopData: CreateWorkshopDto): Promise<AxiosResponse<WorkshopDto>> => {
  return await api.post<WorkshopDto>("/workshops/", workshopData);
};

/**
 * Create a new workshop instance
 * @param workshopId - The ID of the workshop
 * @param workshopInstanceData - The workshop instance data to create
 * @returns The created workshop instance
 */
export const createWorkshopInstance = async (
  workshopId: number,
  workshopInstanceData: CreateWorkshopInstanceDto
): Promise<AxiosResponse<WorkshopInstanceDto>> => {
  return await api.post<WorkshopInstanceDto>(`/workshops/${workshopId}/instances`, workshopInstanceData);
};

/**
 * Add a participant to a workshop instance
 * @param workshopId - The ID of the workshop
 * @param workshopInstanceId - The ID of the workshop instance
 * @param MembersFieldDto - The member to add
 * @returns Empty response on success
 */
export const addWorkshopParticipant = async (
  workshopId: number,
  workshopInstanceId: number,
  participantMemberId: number
): Promise<WorkshopInstanceDto> => {
  const response = await api.post<WorkshopInstanceDto>(
    `/workshops/${workshopId}/instances/${workshopInstanceId}/participants`,
    {
      participantMemberId: participantMemberId,
    }
  );
  return response.data;
};

/**
 * Submit feedback for a workshop instance
 * @param workshopId - The ID of the workshop
 * @param workshopInstanceId - The ID of the workshop instance
 * @param feedbackData - The feedback data to submit
 * @returns Empty response on success
 * */
export const submitWorkshopFeedback = async (
  workshopId: number,
  workshopInstanceId: number,
  feedbackData: WorkshopInstanceUserFeedbackDto
): Promise<AxiosResponse<void>> => {
  return await api.post<void>(`/workshops/${workshopId}/instances/${workshopInstanceId}/feedback`, feedbackData);
};

//-----------------------------------------------------------------------------------------------------------------------
// UPDATE ROUTES

/**
 * Update an existing workshop
 * @param workshopId - The ID of the workshop to update
 * @param workshopData - The workshop data to update
 * @returns The updated workshop
 */
export const updateWorkshop = async (
  workshopId: number,
  workshopData: UpdateWorkshopDto
): Promise<AxiosResponse<WorkshopDto>> => {
  return await api.put<WorkshopDto>(`/workshops/${workshopId}`, workshopData);
};

/**
 * Update an existing workshop instance
 * @param workshopId - The ID of the workshop
 * @param workshopInstanceId - The ID of the workshop instance to update
 * @param workshopInstanceData - The workshop instance data to update
 * @returns The updated workshop instance
 */
export const updateWorkshopInstance = async (
  workshopId: number,
  workshopInstanceId: number,
  workshopInstanceData: UpdateWorkshopInstanceDto
): Promise<AxiosResponse<WorkshopInstanceDto>> => {
  return await api.put<WorkshopInstanceDto>(
    `/workshops/${workshopId}/instances/${workshopInstanceId}`,
    workshopInstanceData
  );
};

/**
 * Close the admission of a workshop instance
 * @param workshopId - The ID of the workshop
 * @param workshopInstanceId - The ID of the workshop instance to close the admission of
 * @param workshopParticipantsData - The workshop participants data to update
 * @returns The updated workshop instance
 */
export const closeAdmission = async (
  workshopId: number,
  workshopInstanceId: number,
  workshopParticipantsData: WorkshopParticipantDto[]
): Promise<AxiosResponse<WorkshopInstanceDto>> => {
  return await api.put<WorkshopInstanceDto>(
    `/workshops/${workshopId}/instances/${workshopInstanceId}/close-admission`,
    workshopParticipantsData
  );
};

/**
 * Complete the attendance of a workshop instance
 * @param workshopId - The ID of the workshop
 * @param workshopInstanceId - The ID of the workshop instance to complete the attendance of
 * @param workshopParticipantsData - The workshop participants data to complete the attendance of
 * @returns The updated workshop instance
 */
export const completeAttendance = async (
  workshopId: number,
  workshopInstanceId: number,
  workshopParticipantsData: WorkshopParticipantDto[]
): Promise<AxiosResponse<WorkshopInstanceDto>> => {
  return await api.put<WorkshopInstanceDto>(
    `/workshops/${workshopId}/instances/${workshopInstanceId}/complete-attendance`,
    workshopParticipantsData
  );
};

/**
 * Close the feedback of a workshop instance
 * @param workshopId - The ID of the workshop
 * @param workshopInstanceId - The ID of the workshop instance to close the feedback of
 * @returns The updated workshop instance
 */
export const closeFeedback = async (
  workshopId: number,
  workshopInstanceId: number
): Promise<AxiosResponse<WorkshopInstanceDto>> => {
  return await api.put<WorkshopInstanceDto>(`/workshops/${workshopId}/instances/${workshopInstanceId}/close-feedback`);
};

/**
 * Reopen the feedback of a workshop instance
 * @param workshopId - The ID of the workshop
 * @param workshopInstanceId - The ID of the workshop instance to reopen the feedback of
 * @returns The updated workshop instance
 */
export const reopenFeedback = async (
  workshopId: number,
  workshopInstanceId: number
): Promise<AxiosResponse<WorkshopInstanceDto>> => {
  return await api.put<WorkshopInstanceDto>(`/workshops/${workshopId}/instances/${workshopInstanceId}/reopen-feedback`);
};

//-----------------------------------------------------------------------------------------------------------------------
// DELETE ROUTES

/**
 * Delete a workshop
 * @param workshopId - The ID of the workshop to delete
 * @returns Empty response on success
 */
export const deleteWorkshop = async (workshopId: number): Promise<AxiosResponse<void>> => {
  return await api.delete<void>(`/workshops/${workshopId}`);
};

/**
 * Delete a workshop instance
 * @param workshopId - The ID of the workshop
 * @param workshopInstanceId - The ID of the workshop instance to delete
 * @returns Empty response on success
 */
export const deleteWorkshopInstance = async (
  workshopId: number,
  workshopInstanceId: number
): Promise<AxiosResponse<void>> => {
  return await api.delete<void>(`/workshops/${workshopId}/instances/${workshopInstanceId}`);
};

/**
 * Remove a participant from a workshop instance
 * @param workshopId - The ID of the workshop
 * @param workshopInstanceId - The ID of the workshop instance
 * @param participantMemberId - The memberId of the participant to remove
 * @returns Empty response on success
 */
export const removeWorkshopParticipant = async (
  workshopId: number,
  workshopInstanceId: number,
  participantMemberId: number
): Promise<void> => {
  const response = await api.delete<void>(`/workshops/${workshopId}/instances/${workshopInstanceId}/participants`, {
    data: { participantMemberId },
  });
  return response.data;
};
